import { createRemoteDataSource, LazyLoadCursorKind } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";
import {
	EventDateFilterType,
	EventDateSortDirection,
	EventPagingInformationModel,
	EventPreviewModel,
	EventSortProperty,
	list
} from "client/Api/Hub/Events/eventViewer";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { isNil } from "client/Misc/utils";

export type EventsFilter = {
	categoryId: number | null;
	activeFilter: ActiveFilter | null;
	dateFilter: EventDateFilterType;
	isNewFilter: HubItemIsNewFilter;
};

export const useDataSource = createRemoteDataSource<
	EventPreviewModel,
	EventsFilter
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<EventPreviewModel>> => {
				const {
					after,
					amount,
					filterState,
					searchTerm,
					sorting,
					signal
				} = options;

				const {
					categoryId,
					activeFilter,
					dateFilter,
					isNewFilter
				} = filterState;

				const [authorId, tagId] = createFilters(activeFilter);
				const sortDirection: EventDateSortDirection | null = createOrder(sorting);

				const pagingInformation: EventPagingInformationModel | null = createPagingInformation(after);

				const response: LazyLoadPageResult<EventPreviewModel> = await list(
					{
						categoryId,
						authorId,
						tagId,
						searchTerm,
						sortDirection,
						filterType: dateFilter,
						pagingInformation,
						amount,
						isNew: hubItemIsNewToFlag[isNewFilter]
					},
					signal
				);

				return response;
			}
		}
	);

const propertyFilterMap: Partial<Record<PropertyPath<EventPreviewModel>, EventSortProperty>> = {
	["startDate"]: EventSortProperty.StartDate
};

export function createOrder(
	sorting: DataSourceSortableState<EventPreviewModel> | undefined
): EventDateSortDirection | null {
	let order: EventDateSortDirection | null = null;

	if (sorting && sorting.direction !== DataSourceSortDirection.None) {
		const property: EventSortProperty | undefined = propertyFilterMap[sorting.propertyName];
		if (isNil(property)) {
			throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
		}

		switch (sorting.direction) {
			case DataSourceSortDirection.Ascending:
				order = EventDateSortDirection.NearToFar;
				break;
			case DataSourceSortDirection.Descending:
				order = EventDateSortDirection.FarToNear;
				break;
			default:
				never(sorting);
		}
	}

	return order;
}

export function createFilters(
	activeFilter: ActiveFilter | null
): [authorId: number | null, tagId: number | null] {
	let authorId: number | null = null;
	let tagId: number | null = null;

	if (activeFilter) {
		switch (activeFilter.kind) {
			case "author":
				authorId = activeFilter.value
					.id;
				break;
			case "tag":
				tagId = activeFilter.value
					.id;
				break;
			default:
				never(activeFilter);
		}
	}

	return [authorId, tagId];
}

export function createPagingInformation(
	after: EventPreviewModel | undefined
): EventPagingInformationModel | null {
	let pagingInformation: EventPagingInformationModel | null = null;

	if (after) {
		pagingInformation = {
			id: after.id,
			startDate: after.startDate
		};
	}

	return pagingInformation;
}
